/* React hooks */
import { useEffect, useState } from "react";
/* Components */
import useData from "../../../hooks/useData";
import { useParams } from "react-router-dom";
import { checkAgencyUniqueUrl, getAgencySiteInfos, updateAgencySisInfos, uploadFile } from "../../../api/admin/agencyReq";
import useAuth from "../../../hooks/useAuth";
import {
    checkRequiredFields,
    fileToBase64,
    getErrorListFromValidation,
    patternListChoices,
    removeErrorList,
    validateFormInput
} from "../../../helper/tools";
import FormButtons from "../../../components/common/FormButtons";
import TiptapForm from "../../../components/common/input-layout/tiptap/TiptapForm";
import Icon from "../../../components/common/Icon";

const weekDays = [
    { value: "monday", label: "Lundi" },
    { value: "tuesday", label: "Mardi" },
    { value: "wednesday", label: "Mercredi" },
    { value: "thursday", label: "Jeudi" },
    { value: "friday", label: "Vendredi" },
    { value: "saturday", label: "Samedi" },
    { value: "sunday", label: "Dimanche" },
];

export default function FormAgencySis({ legend, setShowModal }) {
    const { agencySite, setAgencySite } = useData();
    const { id } = useParams();
    const { auth } = useAuth();
    const token = auth.token;
    const [uniqueUrl, setUniqueUrl] = useState(false);
    const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
    const [values, setValues] = useState();

    const [base64Logo, setBase64Logo] = useState(null);
    const [base64AgencyPicture, setBase64AgencyPicture] = useState(null);

    const onChange = (e) => {
        setValues({ ...values, [e.target.name]: e.target.value })
        if (e.target.type === "checkbox") {
            setValues({ ...values, [e.target.name]: e.target.checked })
        }
        validateFormInput(e)
    };

    const onChangeFlowerPlaces = (e) => {
        const { value, checked } = e.target;
        const current = Array.isArray(values?.flowerPlaces) ? [...values.flowerPlaces] : [];
        if (checked) {
            if (!current.includes(value)) current.push(value);
        } else {
            const idx = current.indexOf(value);
            if (idx !== -1) current.splice(idx, 1);
        }
        setValues({ ...values, flowerPlaces: current });
    };

    const onChangeFlowerAgencyDays = (e) => {
        const { value, checked } = e.target;
        const current = Array.isArray(values?.flowerAgencyDays) ? [...values.flowerAgencyDays] : [];
        if (checked) {
            if (!current.includes(value)) current.push(value);
        } else {
            const idx = current.indexOf(value);
            if (idx !== -1) current.splice(idx, 1);
        }
        setValues({ ...values, flowerAgencyDays: current });
    };

    const onChangeHtml = (e, inputName) => {
        let newContent = e
        setValues({ ...values, [inputName]: newContent })
    }

    const apiAgencySisInfos = async () => {
        const response = await getAgencySiteInfos(token, id);
        setAgencySite(response);
    }

    const handleFileChange = async (e) => {
        const selectedFile = e.target.files[0];
        const fileType = e.target.name;

        try {
            const base64StringConst = await fileToBase64(selectedFile);
            if (fileType === "logo") {
                setBase64Logo(base64StringConst);
            } else if (fileType === "agencypicture") {
                setBase64AgencyPicture(base64StringConst);
            }
            setValues({ ...values, [e.target.name]: selectedFile.name })
        } catch (error) {
            console.error("Erreur lors de la conversion en base64 : ", error);
        }
    };

    // Upload d'un fichier (logo/agencypicture) : appelé depuis handleSubmit, pas via un bouton
    // dédié. Le back génère un nom de fichier différent de celui d'origine (uniqid()), d'où le
    // retour de response?.file_name à utiliser pour la soumission du formulaire complet.
    const apiUploadedFile = async (fileType, base64String, fileName) => {
        // Le back attend un body JSON (file/file_type/file_name), pas un multipart/form-data :
        // le fichier est déjà encodé en base64, pas besoin de FormData.
        const body = {
            file: base64String,
            file_type: fileType,
            file_name: fileName,
        };

        return uploadFile(token, body, id)
            .catch((error) => {
                console.error("Erreur lors de l'upload du fichier:", error);
                return null;
            });
    }

    useEffect(() => {
        setValues(agencySite)
    }, [agencySite]);

    useEffect(() => {
        apiAgencySisInfos()
    }, [id]);

    const apiUniqueUrl = async (body) => {
        const response = await checkAgencyUniqueUrl(token, body)
        let formUrl = document.querySelector(".form-input.url");
        if (response?.code === 200 || response?.code === 100) {
            setUniqueUrl(true)
            if (response?.code === 100) {
                formUrl.classList.remove("invalid")
                formUrl.classList.remove("valid")
            } else {
                formUrl.classList.remove("invalid")
                formUrl.classList.add("valid")
            }
        } else {
            setUniqueUrl(false)
            formUrl.classList.remove("valid")
            formUrl.classList.add("invalid");
        }
    }


    useEffect(() => {
        if (values?.url) {
            let body = {
                agencyId: id,
                url: values?.url
            }
            apiUniqueUrl(body)
            setUniqueUrl(uniqueUrl)
        }
    }, [values?.url]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!checkRequiredFields()) {
            return;
        }

        // Si un nouveau fichier a été sélectionné (prévisualisé mais pas encore envoyé), l'envoyer
        // maintenant avant d'enregistrer le reste du formulaire : le back génère un nom de fichier
        // différent de celui d'origine (uniqid()), donc on utilise le nom retourné pour la suite.
        let logoFileName = values?.logo;
        let agencypictureFileName = values?.agencypicture;

        if (base64Logo) {
            const uploadResponse = await apiUploadedFile("logo", base64Logo, values?.logo);
            if (!uploadResponse?.file_name) {
                getErrorListFromValidation(["Échec de l'envoi du logo."]);
                return;
            }
            logoFileName = uploadResponse.file_name;
        }
        if (base64AgencyPicture) {
            const uploadResponse = await apiUploadedFile("agencypicture", base64AgencyPicture, values?.agencypicture);
            if (!uploadResponse?.file_name) {
                getErrorListFromValidation(["Échec de l'envoi de l'image du site."]);
                return;
            }
            agencypictureFileName = uploadResponse.file_name;
        }

        let body = {
            agencyId: id,
            siteId: values?.siteId,
            siteEnabled: values?.siteEnabled == 1 ? true : false,
            url: values?.url,
            template: values?.template,
            mainColor: values?.mainColor,
            mainColor2: values?.mainColor2,
            siteEmail: values?.siteEmail,
            storeUrl: values?.storeUrl ? values?.storeUrl : "",
            logo: logoFileName,
            logoCss: values?.logoCss,
            agencypicture: agencypictureFileName,
            sendFlowers: values?.sendFlowers || false,
            flowerShop: values?.flowerShop || false,
            agitateur: values?.agitateur || false,
            flowerPlaces: values?.flowerPlaces || [],
            flowerAgencyDays: values?.flowerAgencyDays || [],
            shopWeekend: values?.shopWeekend || false,
            defaultCondolence: values?.defaultCondolence || false,
            lomacoId: values?.lomacoId,
            lomacoLogin: values?.lomacoLogin,
            lomacoPwd: values?.lomacoPwd,
            previewtext: values?.previewtext,
            catchphrase: values?.catchphrase,
            fullText: values?.fullText,
            shopText: values?.shopText,
            funerariumText: values?.funerariumText,
            marbleText: values?.marbleText,
            teamText: values?.teamText,
            welcomeText: values?.welcomeText
        }
        return (
            await updateAgencySisInfos(token, body)
                .then((response) => {
                    removeErrorList()
                    if (!response?.response?.data?.error) {
                        setBase64Logo(null);
                        setBase64AgencyPicture(null);
                        setShowModal(false)
                        apiAgencySisInfos()
                    } else {
                        getErrorListFromValidation(response?.response?.data?.error)
                    }
                }
                ))
    };

    const handleRemoveModal = () => {
        setShowModal(false)
    };

    return (
        <>
            <form method="post" noValidate onSubmit={handleSubmit}>
                <fieldset>
                    <Icon source="fa fa-display" span={legend} />
                    <div className="form-input required">
                        <label htmlFor="siteId">Solution Multi Services</label>
                        <select required="required" name="siteId" value={values?.siteId} onChange={onChange}>
                            <option name="siteId" value="">Choisir une option</option>
                            <option name="siteId" value="1">SMS</option>
                            <option name="siteId" value="2">Annuaire</option>
                            <option name="siteId" value="3">e-Sms</option>
                            <option name="siteId" value="4">Non visible</option>
                            <option name="siteId" value="5">Comparateur</option>
                        </select>
                    </div>
                    <div className="form-input required">
                        <label htmlFor="siteEnabled">Site activé</label>
                        <select required="required" name="siteEnabled" value={values?.siteEnabled == true ? 1 : 0} onChange={onChange}>
                            <option name="siteEnabled" value="">Choisir une option</option>
                            <option name="siteEnabled" value={1}>Oui</option>
                            <option name="siteEnabled" value={0}>Non</option>
                        </select>
                    </div>
                    <div className="form-input url">
                        <label htmlFor="url">URL Site Internet OEF (doit être unique)</label>
                        <input pattern={patternListChoices?.name} type="text" name="url" value={values?.url} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="template">URL du Site Internet Partenaire</label>
                        <input pattern={patternListChoices?.url} type="text" name="template" value={values?.template} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="mainColor">Couleur principale</label>
                        <input type="color" name="mainColor" value={values?.mainColor} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="mainColor2">Couleur secondaire</label>
                        <input type="color" name="mainColor2" value={values?.mainColor2} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="siteEmail">Email du site (email pour "Contactez-nous")</label>
                        <input type="email" name="siteEmail" value={values?.siteEmail} onChange={onChange} />
                    </div>
                    <div className="flex">
                        <div className="form-input agency-picture">
                            <label htmlFor="agencypicture">Image du site</label>
                            <div>
                                {base64AgencyPicture
                                    ? <span><img src={base64AgencyPicture} alt="Photo de présentation de l'agence" /></span>
                                    : values?.agencypicture ? <span><img src={`${IMG_FOLDER}/media/agency_pictures/${values?.agencypicture}`} alt="Photo de présentation de l'agence" /></span> : "Pas d'image sélectionnée"}
                                <input type="file" name="agencypicture" onChange={handleFileChange} />
                            </div>
                        </div>
                        <div className="form-input logo">
                            <label htmlFor="logo">Logo de l'agence</label>
                            <div>
                                {base64Logo
                                    ? <span><img src={base64Logo} alt="Logo de l'agence" /></span>
                                    : values?.logo ? <span><img src={`${IMG_FOLDER}/media/logos/${values?.logo}`} alt="Logo de l'agence" /></span> : "Pas d'image sélectionnée"}
                                <input type="file" name="logo" onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>

                    <div className="flowers-section">
                    <div className="form-input">
                        <label htmlFor="sendFlowers">Offrir des fleurs</label>
                        <input type="checkbox" name="sendFlowers" checked={values?.sendFlowers} onChange={onChange} />
                    </div>
                    {values?.sendFlowers &&
                        <div className="flowers-options">
                            <div className="flowers-inline-option">
                                <div className="form-input">
                                    <label htmlFor="flowerShop">Boutique Prestashop</label>
                                    <input type="checkbox" name="flowerShop" checked={values?.flowerShop} onChange={onChange} />
                                </div>
                                {values?.flowerShop &&
                                    <div className="sub-option-box">
                                        <div className="form-input">
                                            <label htmlFor="storeUrl">Url complète de la boutique</label>
                                            <input type="text" pattern={patternListChoices?.url} name="storeUrl" value={values?.storeUrl || ""} onChange={onChange} />
                                        </div>
                                    </div>
                                }
                            </div>
                            <div className="form-input">
                                <label htmlFor="agitateur">L'agitateur floral</label>
                                <input type="checkbox" name="agitateur" checked={values?.agitateur} onChange={onChange} />
                            </div>
                            {values?.agitateur &&
                                <div className="sub-option-box">
                                    <div className="form-input checkboxes-group">
                                        <label>L'entreprise de Pompes Funèbres autorise la livraison :</label>
                                        <div className="checkbox-options">
                                            <div className="checkbox-option">
                                                <input type="checkbox" name="flowerPlaces" value="flowerFunerarium" id="flowerFunerarium" checked={values?.flowerPlaces?.includes("flowerFunerarium") || false} onChange={onChangeFlowerPlaces} />
                                                <label htmlFor="flowerFunerarium">Au funérarium de l'agence</label>
                                            </div>
                                            <div className="checkbox-option">
                                                <input type="checkbox" name="flowerPlaces" value="flowerCeremony" id="flowerCeremony" checked={values?.flowerPlaces?.includes("flowerCeremony") || false} onChange={onChangeFlowerPlaces} />
                                                <label htmlFor="flowerCeremony">Au lieu de cérémonie</label>
                                            </div>
                                            <div className="checkbox-option">
                                                <input type="checkbox" name="flowerPlaces" value="flowerRassemblement" id="flowerRassemblement" checked={values?.flowerPlaces?.includes("flowerRassemblement") || false} onChange={onChangeFlowerPlaces} />
                                                <label htmlFor="flowerRassemblement">Au lieu de rassemblement</label>
                                            </div>
                                            <div className="checkbox-option">
                                                <input type="checkbox" name="flowerPlaces" value="flowerRecueillement" id="flowerRecueillement" checked={values?.flowerPlaces?.includes("flowerRecueillement") || false} onChange={onChangeFlowerPlaces} />
                                                <label htmlFor="flowerRecueillement">Au lieu de recueillement</label>
                                            </div>
                                            <div className="checkbox-option">
                                                <input type="checkbox" name="flowerPlaces" value="flowerAgency" id="flowerAgency" checked={values?.flowerPlaces?.includes("flowerAgency") || false} onChange={onChangeFlowerPlaces} />
                                                <label htmlFor="flowerAgency">À l'agence</label>
                                            </div>
                                        </div>
                                    </div>
                                    {values?.flowerPlaces?.includes("flowerAgency") &&
                                        <div className="sub-option-box">
                                            <div className="form-input checkboxes-group">
                                                <label>Jours ouvrés pour la livraison à l'agence :</label>
                                                <div className="checkbox-options">
                                                    {weekDays.map(day => (
                                                        <div className="checkbox-option" key={day.value}>
                                                            <input type="checkbox" name="flowerAgencyDays" value={day.value} id={`flowerAgencyDay-${day.value}`} checked={values?.flowerAgencyDays?.includes(day.value) || false} onChange={onChangeFlowerAgencyDays} />
                                                            <label htmlFor={`flowerAgencyDay-${day.value}`}>{day.label}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            }

                        </div>
                    }
                    </div>
                    <div className="form-input">
                        <label htmlFor="shopWeekend">Boutique ouverte le week-end</label>
                        <input type="checkbox" name="shopWeekend" checked={values?.shopWeekend} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="defaultCondolence">Condoléance par défaut</label>
                        <input type="checkbox" name="defaultCondolence" checked={values?.defaultCondolence} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="lomacoId">Identifiant Lomaco</label>
                        <input type="text" name="lomacoId" value={values?.lomacoId} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="lomacoLogin">Login Lomaco</label>
                        <input type="text" name="lomacoLogin" value={values?.lomacoLogin} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="lomacoPwd">Mot de passe Lomaco</label>
                        <input type="password" name="lomacoPwd" value={values?.lomacoPwd} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="logoCss">CSS du logo</label>
                        <textarea name="logoCss" value={values?.logoCss} onChange={onChange} />
                    </div>
                    <div className="form-input html-textarea">
                        <label htmlFor="previewtext">Texte de présentation</label>
                        <TiptapForm content={values?.previewtext} onChange={(e) => onChangeHtml(e, 'previewtext')} />
                    </div>
                    <div className="form-input html-textarea">
                        <label htmlFor="fullText">Texte complet</label>
                        <TiptapForm content={values?.fullText} onChange={(e) => onChangeHtml(e, 'fullText')} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="shopText">Magasin</label>
                        <textarea name="shopText" value={values?.shopText} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="funerariumText">Funérarium</label>
                        <textarea name="funerariumText" value={values?.funerariumText} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="marbleText">Marbrerie</label>
                        <textarea name="marbleText" value={values?.marbleText} onChange={onChange} />
                    </div>
                    <div className="form-input">
                        <label htmlFor="teamText">Notre équipe</label>
                        <textarea name="teamText" value={values?.teamText} onChange={onChange} />
                    </div>
                    <div className="form-input html-textarea">
                        <label htmlFor="welcomeText">Texte page "Bienvenue chez nous"</label>
                        <TiptapForm content={values?.welcomeText} onChange={(e) => onChangeHtml(e, 'welcomeText')} />
                    </div>
                    <div className="errors"></div>
                    <div className="buttons-container">
                        <FormButtons handleRemoveModal={handleRemoveModal} />
                    </div>
                </fieldset>
            </form>
        </>

    )
}